import { Box, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import type { AffiliateFilters } from '@/types/filters';

interface AffiliateStatusFilterProps {
  value: AffiliateFilters['status'];
  onChange: (status: AffiliateFilters['status']) => void;
}

export const AffiliateStatusFilter: React.FC<AffiliateStatusFilterProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [tempStatus, setTempStatus] = useState<AffiliateFilters['status']>(value || 'all');

  const statusOptions = useMemo(
    () =>
      [
        { value: 'all', label: t('affiliates.filters.status.all') },
        { value: 'active', label: t('affiliates.filters.status.active') },
        { value: 'inactive', label: t('affiliates.filters.status.inactive') },
      ] as const,
    [t]
  );

  useEffect(() => {
    setTempStatus(value || 'all');
  }, [value]);

  const handleApply = () => {
    onChange(tempStatus);
  };

  const handleClear = () => {
    setTempStatus('all');
    onChange('all');
  };

  const handleStatusSelect = (status: AffiliateFilters['status']) => {
    setTempStatus(status);
  };

  const isFilterActive = !!(value && value !== 'all');
  const selectedOption = statusOptions.find((option) => option.value === value);

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('affiliates.filters.status.label')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>
            {selectedOption?.label || t('affiliates.filters.status.all')}
          </FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="200px">
            <FilterButtonMenuHeader
              title={t('affiliates.filters.status.title')}
              onClearAll={handleClear}
            />
            <Box p={3}>
              <VStack gap={0.5} align="stretch">
                {statusOptions.map((option) => (
                  <FilterMenuItem
                    key={option.value}
                    onClick={() => handleStatusSelect(option.value)}
                    isSelected={tempStatus === option.value}
                  >
                    <Text fontSize="sm">{option.label}</Text>
                  </FilterMenuItem>
                ))}
              </VStack>
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
